// Fleet theme — sign-in pages (website side).
// Builds the split layout: a brand panel on the start side, the form on the end side.
// Other website / portal pages are left untouched.

(() => {
	const AUTH_PATHS = ["login", "update-password", "forgot-password"];
	const RTL_LANGS = ["ar", "he", "fa", "ps"];

	const TEXT = {
		en: {
			headline: "Every machine. Every job. One place.",
			sub: "Assets, maintenance, operations and compliance, connected in real time.",
			welcome: "Welcome back. Sign in to continue.",
			secure: "Secure sign-in",
		},
		ar: {
			headline: "كل معدة. كل أمر عمل. في مكان واحد.",
			sub: "الأصول والصيانة والعمليات والامتثال، مترابطة لحظة بلحظة.",
			welcome: "مرحبًا بعودتك. سجّل الدخول للمتابعة.",
			secure: "تسجيل دخول آمن",
		},
	};

	const esc = (s) =>
		String(s == null ? "" : s).replace(
			/[&<>"']/g,
			(c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
		);

	// Values the theme stylesheet exposes as CSS custom properties (Fleet Settings > Appearance)
	const css_var = (name) => {
		let v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
		if (v.length > 1 && v[0] === '"' && v[v.length - 1] === '"') {
			v = v.slice(1, -1).replace(/\\(.)/g, "$1");
		}
		return v;
	};

	// Decorative node network
	const ART = `
		<svg class="fe-auth-art" viewBox="0 0 600 600" fill="none" aria-hidden="true">
			<g stroke="currentColor" stroke-opacity=".22" stroke-width="1.5">
				<path d="M80 420 220 330 360 380 470 260 560 320M220 330 300 200 470 260M300 200 420 110M360 380 420 520 560 470 560 320M80 420 160 540 420 520M300 200 180 140 80 220 80 420M420 110 540 160 470 260"/>
			</g>
			<g fill="currentColor" fill-opacity=".6">
				<circle cx="80" cy="420" r="5"/><circle cx="220" cy="330" r="6"/><circle cx="360" cy="380" r="5"/>
				<circle cx="560" cy="320" r="5"/><circle cx="420" cy="520" r="6"/><circle cx="560" cy="470" r="4"/>
				<circle cx="160" cy="540" r="4"/><circle cx="420" cy="110" r="5"/><circle cx="180" cy="140" r="4"/>
				<circle cx="80" cy="220" r="5"/><circle cx="540" cy="160" r="4"/>
			</g>
			<g style="color: rgb(var(--fe-accent-glow-rgb))">
				<circle cx="300" cy="200" r="22" stroke="currentColor" stroke-width="3" stroke-opacity=".9"/>
				<circle cx="300" cy="200" r="9" fill="currentColor"/>
				<circle cx="470" cy="260" r="10" fill="currentColor" fill-opacity=".85"/>
				<circle cx="470" cy="260" r="26" fill="currentColor" fill-opacity=".12"/>
			</g>
		</svg>`;

	function build() {
		const path = document.body && document.body.dataset.path;
		if (!AUTH_PATHS.includes(path) || document.querySelector(".fe-auth-brand")) return;

		const lang = (document.documentElement.lang || "en").toLowerCase();
		const stock = lang.startsWith("ar") ? TEXT.ar : TEXT.en;
		// custom copy from the settings wins; it is already translated server-side
		const t = {
			headline: css_var("--fe-login-headline") || stock.headline,
			sub: css_var("--fe-login-subline") || stock.sub,
			welcome: css_var("--fe-login-welcome") || stock.welcome,
			secure: stock.secure,
		};

		// Website pages ship RTL stylesheets but no dir attribute (the desk sets it);
		// the split layout uses logical properties, so give it the real direction.
		if (RTL_LANGS.includes(lang.split("-")[0])) {
			document.documentElement.setAttribute("dir", "rtl");
		}

		// Brand name and logo resolve server-side (settings → Website Settings → company).
		// The page title ("Acme - Login") is only a fallback for sites without the stylesheet.
		const parts = document.title.split(" - ");
		const brand = css_var("--fe-brand-name") || (parts.length > 1 ? parts[0].trim() : "");
		const logo_el = document.querySelector(".page-card-head .app-logo");
		const logo = css_var("--fe-brand-logo") || (logo_el ? logo_el.getAttribute("src") : "");

		// Frappe's own headings ("Login to <app name>") use Website Settings → App Name;
		// keep them consistent with the brand shown in the panel.
		const site_name = parts.length > 1 ? parts[0].trim() : "";
		if (brand && site_name && site_name !== brand) {
			document.querySelectorAll(".page-card-head h4").forEach((h) => {
				if (h.childElementCount === 0) h.textContent = h.textContent.split(site_name).join(brand);
			});
		}

		document.body.classList.add("fe-auth");
		document.body.insertAdjacentHTML(
			"afterbegin",
			`<aside class="fe-auth-brand">
				<div class="fe-auth-glow"></div>
				${ART}
				<div class="fe-auth-brand-top">
					${logo ? `<span class="fe-auth-logo"><img src="${esc(logo)}" alt=""></span>` : ""}
					${brand ? `<span class="fe-auth-name">${esc(brand)}</span>` : ""}
				</div>
				<div class="fe-auth-pitch">
					<h1>${esc(t.headline)}</h1>
					<p>${esc(t.sub)}</p>
				</div>
				<div class="fe-auth-foot">
					<span>${brand ? `© ${new Date().getFullYear()} ${esc(brand)}` : ""}</span>
					<span class="fe-auth-secure">
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
						${esc(t.secure)}
					</span>
				</div>
			</aside>`
		);

		// Subtitle under the sign-in heading
		const head = document.querySelector(".for-login .page-card-head");
		if (head && !head.querySelector(".fe-auth-sub")) {
			head.insertAdjacentHTML("beforeend", `<p class="fe-auth-sub">${esc(t.welcome)}</p>`);
		}

		// Keep Frappe's own language picker, moved out of the (hidden) navbar
		const switcher = document.getElementById("language-switcher");
		const wrapper = document.querySelector(".page-content-wrapper");
		if (switcher && wrapper) {
			const bar = document.createElement("div");
			bar.className = "fe-auth-top";
			bar.appendChild(switcher);
			wrapper.prepend(bar);
		}
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", build);
	} else {
		build();
	}
})();
